import type { FeatureConfig } from '@skynet/feature-config';
import { KeyOutlined } from '@ant-design/icons';
import { MARKETING_DETAIL_ROUTE_PATH, MARKETING_LIST_URL, MARKETING_ROUTE_PATH } from './routes';

/**
 * Feature manifest for the admin Marketing campaigns list and campaign detail pages. The dashboard
 * shell reads this single source of truth instead of hardcoding the route,
 * navigation entry, header label and Cerbos action across its routing,
 * navigation, header and access-control modules.
 *
 * `route.load` and `detailRoute.load` are dynamic-import thunks, so importing this config for its
 * nav/header metadata never pulls the page implementation into the main bundle.
 *
 * The route path, nav url and WS-login skip path come from this package's own
 * `./routes`, which also supplies the URLs the page hands to
 * `@skynet/sky-marketing`'s components. The config imports nothing from
 * `@skynet/sky-marketing`, so reading it never drags the Marketing UI onto the
 * boot path.
 *
 * `section`, `nav.key` and `cerbosAction` stay literal: they are the identifier
 * the Cerbos policy is written against (`read:admin:marketing-campaigns`), and
 * renaming any of them hides the nav item until the policy is renamed too.
 */
export const marketingConfig = {
  domain: 'Admin',
  section: 'marketing-campaigns',
  route: {
    path: MARKETING_ROUTE_PATH,
    load: async () => ({ default: (await import('./Marketing')).Marketing }),
  },
  detailRoute: {
    path: MARKETING_DETAIL_ROUTE_PATH,
    load: async () => ({ default: (await import('./MarketingPage')).MarketingPage }),
  },
  nav: {
    key: 'admin.marketing-campaigns',
    label: 'Marketing',
    icon: <KeyOutlined />,
    url: MARKETING_LIST_URL,
  },
  header: { label: 'Marketing Campaigns' },
  cerbosAction: 'read:admin:marketing-campaigns',
  skipWsLoginPaths: [MARKETING_LIST_URL],
} as const satisfies FeatureConfig;
